import { classMerge } from '@/chassis/core/common-helpers/class-merge';
import { Link, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import { Children, cloneElement, createContext, isValidElement, useContext, useMemo, type ReactNode } from 'react';
import { sideMenuConfig, type SideMenuEntry } from '@/access-control.config';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { useLanguageControls } from '@/chassis/ui/i18n/useLanguage';
import { translateMenuTitle } from '@/side-menu.i18n';

interface BreadcrumbProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'nav'> {
    light?: boolean;
    children?: React.ReactElement | React.ReactElement[] | null;
    auto?: boolean;
}

interface BreadcrumbItem {
    title: ReactNode;
    pathname?: string;
}

const breadcrumbContext = createContext<{
    light?: boolean;
}>({
    light: undefined,
});

const routeSegmentTitles: Record<string, string> = {
    licitacoes: 'Licitações',
    cidadaos: 'Cidadãos',
    fornecedores: 'Fornecedores',
    'orgaos-publicos': 'Órgãos Públicos',
    mydashboard: 'Meu Painel',
    myprofile: 'Meu Perfil',
    myaccountdetails: 'Minha Conta',
    myfaqs: 'FAQs',
    mytickets: 'Meus Chamados',
    myrepresentations: 'Minhas Representações',
    representationrequests: 'Solicitações de Vínculo',
    planschoice: 'Escolha de Planos',
    tickets: 'Tickets',
    documentos: 'Documentos',
    notificacoes: 'Notificações',
    agenda: 'Agenda',
    mensagens: 'Mensagens',
    perfil: 'Perfil',
    configuracoes: 'Configurações',
    cadastro: 'Cadastro',
    editar: 'Editar',
    visualizar: 'Visualizar',
    propostas: 'Propostas',
    lances: 'Lances',
    contratos: 'Contratos',
    aprovacoes: 'Aprovações',
    adjudicacoes: 'Adjudicações',
    especialidades: 'Especialidades Profissionais',
    setores: 'Setores Públicos',
    'categorias-fornecimento': 'Categorias de Fornecimento',
    'categorias-itens': 'Categorias de Itens',
    'sessoes-leilao': 'Sessões de Leilão',
    enviados: 'Documentos Enviados',
    gerados: 'Documentos Gerados',
    templates: 'Templates',
    grupos: 'Grupos',
    seguranca: 'Segurança',
    sistema: 'Sistema',
    domains: 'Domínios',
    '1.1-general-module-generalparameters': 'Parâmetros Gerais',
    '1.2-general-module-internationalization': 'Internacionalização',
    '1.3-general-module-notification': 'Comunicação',
    '1.4-general-module-document': 'Documentos',
    '1.5-general-module-media': 'Mídia',
    '1.6-general-module-help': 'Central de Ajuda',
    '1.7-general-module-ticket': 'Atendimento',
    '2.1-auth-module-authentication': 'Autenticação',
    '2.2-auth-module-authorization': 'Autorização',
    '2.3-auth-module-tracking': 'Monitoramento',
    '3.1-registration-module-citizen': 'Cadastro Cidadão',
    '3.2-registration-module-company': 'Cadastro Empresas',
    '3.3-registration-module-representation': 'Cadastro Representação',
    '4.1-publictender-module-request': 'Planejamento',
    '4.2-publictender-module-calendar': 'Calendário',
    '4.3-publictender-module-chat': 'Comunicações',
    '4.4-publictender-module-bidding': 'Sessões e Lances',
    '4.5-publictender-module-approval': 'Aprovação',
    '4.6-publictender-module-contestation': 'Contestação',
    '5.1-subscription-module-catalog': 'Catálogo',
    '5.2-subscription-module-account': 'Conta',
    '5.3-subscription-module-order': 'Pedido',
    '5.4-subscription-module-payment': 'Pagamento',
    '6.1-integrations-module-publictender': 'Integrações PNCP',
    '6.2-integrations-module-subscription': 'Integrações Assinaturas',
    '6.3-integrations-module-digitalsignature': 'Integrações Assinatura Digital',
    form: 'Formulário',
    // Substantivo, como "Edição" e "Visualização": "Novo" solto não concorda com entidade feminina.
    novo: 'Cadastro',
    new: 'Cadastro',
    authmoduletracking: 'Monitoramento',
    credentials: 'Credenciais',
    details: 'Detalhes',
    detalhar: 'Detalhes',
    detalhes: 'Detalhes',
};

const isIdSegment = (segment: string): boolean => /^[0-9a-fA-F-]{6,}$/.test(segment);
const isDomainRootSegment = (segment: string): boolean => segment.includes('-module-');

/** Mapa de redirecionamento de caminhos intermediarios do breadcrumb.
 *  Quando o breadcrumb auto-gera um link para um caminho intermediario,
 *  se houver override aqui, o link apontara para o override. */
const breadcrumbPathOverrides: Record<string, string> = {
    '/publictenders': '/publictenders/search',
};

function formatSegment(segment: string): string {
    if (/^\d+$/.test(segment)) {
        return `#${segment}`;
    }
    return segment
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function buildMenuPathTitleMap(entries: SideMenuEntry[], language: Language, map: Record<string, string> = {}): Record<string, string> {
    entries.forEach((entry) => {
        if (typeof entry === 'string') {
            return;
        }

        if (entry.pathname) {
            map[entry.pathname] = translateMenuTitle(entry.pathname, language, entry.title ?? '', entry.i18nTitle);
        }

        if (entry.subMenu) {
            buildMenuPathTitleMap(entry.subMenu, language, map);
        }
    });

    return map;
}

function Breadcrumb({ className, light, children, auto = false }: BreadcrumbProps) {
    const location = useLocation();
    const { language } = useLanguageControls();

    const menuPathTitleMap = useMemo(() => buildMenuPathTitleMap(sideMenuConfig, language), [language]);

    const generateBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
        const segments = pathname.split('/').filter(Boolean);
        const breadcrumbs: BreadcrumbItem[] = [];
        const homePath = '/';
        const dashboardLabel = menuPathTitleMap[homePath] ?? 'Home';
        const dashboardTitle = (
            <span className="inline-flex items-center" aria-label={dashboardLabel}>
                <Home className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{dashboardLabel}</span>
            </span>
        );

        breadcrumbs.push({
            title: dashboardTitle,
            pathname: segments.length > 0 ? homePath : undefined,
        });

        const pathParts: string[] = [];

        for (let index = 0; index < segments.length; index += 1) {
            const segment = segments[index];

            if (index === 0 && segment === 'dashboard') {
                breadcrumbs[0].pathname = undefined;
                pathParts.push(segment);
                continue;
            }

            if (segment === 'domains') {
                pathParts.push(segment);
                continue;
            }

            if (segment === 'edit' || segment === 'view') {
                const next = segments[index + 1];
                const isEdit = segment === 'edit';
                const label = isEdit ? 'Edição' : 'Visualização';

                pathParts.push(segment);
                const path = `/${pathParts.join('/')}`;

                breadcrumbs.push({
                    title: label,
                    pathname: index < segments.length - (next ? 2 : 1) ? path : undefined,
                });

                if (next) {
                    index += 1; // skip id segment in breadcrumb trail
                }

                continue;
            }

            if (isIdSegment(segment)) {
                continue;
            }

            pathParts.push(segment);
            const path = `/${pathParts.join('/')}`;
            // As telas de cadastro administrativo saíram do menu lateral, mas o título delas
            // continua no dicionário de rotas; sem isso a trilha mostrava "Documentgroups".
            const exactTitle = menuPathTitleMap[path] ?? (translateMenuTitle(path, language, '') || undefined);
            const segmentTitle = routeSegmentTitles[segment] ?? (translateMenuTitle(`/${segment}`, language, '') || undefined);
            const title = exactTitle ?? segmentTitle ?? formatSegment(segment);
            const resolvedPath = breadcrumbPathOverrides[path] ?? path;

            breadcrumbs.push({
                title,
                pathname: index < segments.length - 1 && !isDomainRootSegment(segment) ? resolvedPath : undefined,
            });
        }

        return breadcrumbs;
    };

    if (auto) {
        const breadcrumbs = generateBreadcrumbs(location.pathname);

        return (
            <breadcrumbContext.Provider value={{ light }}>
                <nav className={classMerge(['flex', className])} aria-label="breadcrumb">
                    <ol className={classMerge(['flex items-center text-theme-1 dark:text-slate-300', { 'text-white/90': light }])}>
                        {breadcrumbs.map((item, index) => (
                            <BreadcrumbLink
                                key={`crumb-${index}`}
                                to={item.pathname}
                                active={index === breadcrumbs.length - 1}
                                itemIndex={index}
                            >
                                {item.title}
                            </BreadcrumbLink>
                        ))}
                    </ol>
                </nav>
            </breadcrumbContext.Provider>
        );
    }

    // Original breadcrumb implementation for manual use
    return (
        <breadcrumbContext.Provider
            value={{
                light: light,
            }}
        >
            <nav className={classMerge(['flex', className])} aria-label="breadcrumb">
                <ol className={classMerge(['flex items-center text-theme-1 dark:text-slate-300', { 'text-white/90': light }])}>
                    {Children.toArray(children)
                        .filter(isValidElement)
                        .map((item, index) => cloneElement(item as React.ReactElement, { itemIndex: index, key: index } as any))}
                </ol>
            </nav>
        </breadcrumbContext.Provider>
    );
}

interface LinkProps extends React.PropsWithChildren, React.ComponentPropsWithoutRef<'li'> {
    to?: string;
    active?: boolean;
    itemIndex?: number;
}

function BreadcrumbLink({ className, to, active = false, children, itemIndex = 0, ...attr }: LinkProps) {
    const breadcrumb = useContext(breadcrumbContext);
    const isMuted = active || !to;

    const content = to ? <Link to={to}>{children}</Link> : <span className="cursor-default">{children}</span>;

    return (
        <li
            className={classMerge([
                className,
                itemIndex > 0 && 'relative ml-5 pl-0.5',
                !to && 'cursor-default',
                !breadcrumb.light &&
                    itemIndex > 0 &&
                    "before:content-[''] before:w-[14px] before:h-[14px] before:bg-chevron-black before:transform before:rotate-[-90deg] before:bg-[length:100%] before:-ml-[1.125rem] before:absolute before:my-auto before:inset-y-0",
                breadcrumb.light &&
                    itemIndex > 0 &&
                    "before:content-[''] before:w-[14px] before:h-[14px] before:bg-chevron-white before:transform before:rotate-[-90deg] before:bg-[length:100%] before:-ml-[1.125rem] before:absolute before:my-auto before:inset-y-0",
                itemIndex > 0 && 'dark:before:bg-chevron-white',
                !breadcrumb.light && isMuted && 'text-slate-600 cursor-text dark:text-slate-400',
                breadcrumb.light && isMuted && 'text-white/70',
            ])}
            {...attr}
        >
            {content}
        </li>
    );
}

Breadcrumb.Link = BreadcrumbLink;

export default Breadcrumb;
